import { useState } from "react";
import { useTranslation } from "react-i18next";
import { convertText } from "@/utils/langConvert";
import { Link, useNavigate } from "react-router-dom";
import { usePwaInstall, type InstallPlatform } from "@/hooks/usePwaInstall";
import { useAuth } from "@/context/AuthContext";
import { menuItems, BTN_BG } from "../data";
import { track } from "@/utils/track";
import { DOWNLOAD_PAGE_PATH } from "@/config/download";

// 临时隐藏的菜单项 ID（尚未上线的功能）
const HIDDEN_MENU_IDS = new Set(["zodiac", "tarot", "ziwei"]);

interface SidebarProps {
  activeId: string;
  onSelect: (id: string) => void;
  isLight: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  isMobileDrawer?: boolean;
  onCloseDrawer?: () => void;
}

const PLATFORM_TIPS: Record<InstallPlatform, { title: string; body: string; icon: string }> = {
  "chrome-android": {
    title: "添加到主屏幕",
    body: "请点击 Chrome 右上角菜单 ⋮，选择「添加到主屏幕」。",
    icon: "ri-android-line",
  },
  "chrome-desktop": {
    title: "安装到桌面",
    body: "请点击 Chrome 右上角菜单 ⋮ → 更多工具 → 创建快捷方式，或直接在地址栏右侧点击「安装」图标。",
    icon: "ri-chrome-line",
  },
  edge: {
    title: "安装到桌面",
    body: "请点击 Edge 右上角菜单 ⋯ → 应用 → 将此站点作为应用安装。",
    icon: "ri-edge-new-line",
  },
  "safari-ios": {
    title: "添加到主屏幕",
    body: "请点击 Safari 底部分享按钮，然后选择「添加到主屏幕」。",
    icon: "ri-apple-fill",
  },
  "safari-desktop": {
    title: "添加到 Dock",
    body: "macOS Safari 暂不支持 PWA 安装。请使用 Chrome 或 Edge 浏览器访问，或手动将网页拖拽到 Dock。",
    icon: "ri-apple-fill",
  },
  firefox: {
    title: "添加到桌面",
    body: "Firefox 暂不支持直接安装 PWA。请使用 Chrome 或 Edge 浏览器访问此页面。",
    icon: "ri-firefox-line",
  },
  other: {
    title: "添加到桌面",
    body: "请使用 Chrome、Edge 或 Safari 浏览器访问，然后从浏览器菜单中选择「添加到主屏幕」或「安装」。",
    icon: "ri-global-line",
  },
};

export default function Sidebar({
  activeId,
  onSelect,
  isLight,
  collapsed,
  onToggleCollapse,
  isMobileDrawer,
  onCloseDrawer,
}: SidebarProps) {
  const isCollapsed = collapsed && !isMobileDrawer;
  const { i18n } = useTranslation();
  const trd = (text: string) => convertText(text, i18n.language);
  const { canInstall, isInstalled, platform, promptInstall } = usePwaInstall();
  const { isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tipOpen, setTipOpen] = useState(false);
  const [tipPlatform, setTipPlatform] = useState<InstallPlatform | null>(null);

  const handleAddToHome = async () => {
    if (isInstalled) return;

    const result = await promptInstall();

    if (!result.success && result.platform !== "installed") {
      setTipPlatform(result.platform);
      setTipOpen(true);
      setTimeout(() => {
        setTipOpen(false);
        setTipPlatform(null);
      }, 6000);
    }
  };

  const tip = tipPlatform ? PLATFORM_TIPS[tipPlatform] : null;

  return (
    <aside
      className={`h-full flex flex-col shrink-0 border-r transition-all duration-300 ease-out ${
        isLight
          ? "bg-white border-[#e5e5ea]"
          : "bg-cosmic-900 border-white/10"
      } ${
        isCollapsed
          ? "w-16"
          : isMobileDrawer
            ? "w-64"
            : "w-64"
      }`}
    >
      {/* Header: Logo + Collapse toggle */}
      <div
        className={`flex items-center shrink-0 ${
          isCollapsed ? "px-2 py-5 justify-center" : "px-5 pt-5 pb-3 justify-between"
        }`}
      >
        {isCollapsed ? (
          <div className="flex flex-col items-center gap-3">
            <Link
              to="/"
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-opacity hover:opacity-80 ${
                isLight ? "bg-[#4a4a4c]" : "bg-gold-500/20"
              }`}
              title="命理乾坤"
            >
              <img
                src="/images/app-icon-512.webp"
                alt="命理乾坤"
                className="w-6 h-6 object-contain"
              />
            </Link>
            <button
              onClick={onToggleCollapse}
              className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                isLight
                  ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
                  : "text-silver-400 hover:bg-white/5"
              }`}
              title="展开菜单"
            >
              <i className="ri-side-bar-fill text-lg" />
            </button>
          </div>
        ) : (
          <>
            <Link
              to="/"
              className={`flex items-center gap-2.5 hover:opacity-80 transition-opacity ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                  isLight ? "bg-[#4a4a4c]" : "bg-gold-500/20"
                }`}
              >
                <img
                  src="/images/app-icon-512.webp"
                  alt="命理乾坤"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-bold tracking-wider leading-tight">
                  命理乾坤
                </span>
                <span className={`text-[10px] font-medium tracking-wider mt-0.5 ${isLight ? 'text-[#8a6a3e]' : 'text-gold-400/80'}`}>
                  "洞悉命理，掌控乾坤"
                </span>
              </div>
            </Link>

            {isMobileDrawer ? (
              <button
                onClick={onCloseDrawer}
                className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  isLight
                    ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
                    : "text-silver-400 hover:bg-white/5"
                }`}
              >
                <i className="ri-close-line text-lg" />
              </button>
            ) : (
              <button
                onClick={onToggleCollapse}
                className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
                  isLight
                    ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
                    : "text-silver-400 hover:bg-white/5"
                }`}
                title={isCollapsed ? "展开菜单" : "收起菜单"}
              >
                <i
                  className={`${
                    isCollapsed
                      ? "ri-side-bar-fill"
                      : "ri-side-bar-line"
                  } text-lg`}
                />
              </button>
            )}
          </>
        )}
      </div>

      {/* Menu */}
      <nav className="flex-1 overflow-y-auto">
        <div className={isCollapsed ? "px-2 py-2 space-y-3" : "px-3 py-2 space-y-1"}>
          {menuItems
            .filter((item) => !HIDDEN_MENU_IDS.has(item.id))
            .map((item) => {
              const isActive = activeId === item.id;

              return (
                <div key={item.id}>
                  <button
                    onClick={() => {
                      onSelect(item.id);
                      if (isMobileDrawer) onCloseDrawer?.();
                    }}
                    className={`w-full flex items-center rounded-lg text-sm transition-colors cursor-pointer ${
                      isCollapsed
                        ? "justify-center py-3 px-2"
                        : "gap-3 px-3 py-2.5"
                    } ${
                      isActive
                        ? isLight
                          ? `bg-[${BTN_BG}] text-white`
                          : "bg-gold-500/15 text-gold-400"
                        : isLight
                          ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
                          : "text-silver-400 hover:bg-white/5"
                    }`}
                    title={isCollapsed ? trd(item.label) : undefined}
                  >
                    <span
                      className={`flex items-center justify-center ${
                        isCollapsed ? "w-6 h-6" : "w-5 h-5"
                      }`}
                    >
                      <i className={`${item.icon} text-base`} />
                    </span>
                    {!isCollapsed && (
                      <span className="flex-1 text-left">{trd(item.label)}</span>
                    )}
                  </button>
                </div>
              );
            })}
        </div>

        {!isCollapsed && (
          <div className="mt-6 px-3">
            <p
              className={`text-[11px] font-medium uppercase tracking-wider mb-2 ${
                isLight ? "text-[#9e9ea2]" : "text-silver-400"
              }`}
            >
              {trd("最近对话")}
            </p>
            <p
              className={`text-xs ${
                isLight ? "text-[#c5c5c7]" : "text-silver-500"
              }`}
            >
              {trd("暂无对话记录")}
            </p>
          </div>
        )}
      </nav>

      {/* Bottom actions */}
      <div
        className={`shrink-0 border-t ${
          isLight ? "border-gray-100" : "border-white/5"
        } ${isCollapsed ? "px-2 py-3 space-y-3" : "px-3 py-3 space-y-2"}`}
      >
        <Link
          to={DOWNLOAD_PAGE_PATH}
          onClick={() => track("download_click", { toolId: "sidebar-download" })}
          className={`w-full flex items-center rounded-lg text-sm transition-colors cursor-pointer ${
            isCollapsed
              ? "justify-center py-2 px-1"
              : "gap-2.5 px-3 py-2"
          } ${
            isLight
              ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
              : "text-silver-400 hover:bg-white/5"
          }`}
          title={isCollapsed ? trd("下载 APP") : undefined}
        >
          <span className="flex items-center justify-center w-5 h-5">
            <i className="ri-smartphone-line text-base" />
          </span>
          {!isCollapsed && <span>{trd("下载 APP")}</span>}
        </Link>

        <button
          onClick={handleAddToHome}
          className={`w-full flex items-center rounded-lg text-sm transition-colors cursor-pointer ${
            isCollapsed
              ? "justify-center py-2 px-1"
              : "gap-2.5 px-3 py-2"
          } ${
            isLight
              ? "text-[#6e6e73] hover:bg-[#f5f5f7]"
              : "text-silver-400 hover:bg-white/5"
          } ${isInstalled ? "opacity-50 cursor-default" : ""}`}
          title={isCollapsed ? trd("添加到桌面") : undefined}
        >
          <span className="flex items-center justify-center w-5 h-5">
            <i className={`${isInstalled ? "ri-check-line" : "ri-download-line"} text-base`} />
          </span>
          {!isCollapsed && <span>{isInstalled ? trd("已添加到桌面") : trd("添加到桌面")}</span>}
        </button>

        {/* iOS 安装引导提示 */}
        {tipOpen && !isCollapsed && (
          <div
            className={`mt-2 px-3 py-2.5 rounded-lg text-xs leading-relaxed ${
              isLight
                ? "bg-[#f5f5f7] text-[#6e6e73] border border-[#e5e5ea]"
                : "bg-cosmic-800 text-silver-400 border border-white/10"
            }`}
          >
            <p className="font-medium mb-1">
              <i className={`${tip?.icon} mr-1`} />
              {trd(tip?.title ?? "")}
            </p>
            <p>
              {trd(tip?.body ?? "")}
            </p>
          </div>
        )}

        {isLoggedIn ? (
          <div
            className={`flex items-center ${
              isCollapsed ? "justify-center px-1" : "gap-2.5 px-3 py-1"
            }`}
          >
            <div
              className={`rounded-full flex items-center justify-center shrink-0 ${
                isLight ? "bg-[#e5e5ea]" : "bg-cosmic-800"
              } ${isCollapsed ? "w-8 h-8" : "w-7 h-7"}`}
            >
              <i
                className={`${
                  isCollapsed ? "ri-user-3-line" : "ri-user-line"
                } ${isLight ? "text-[#6e6e73]" : "text-silver-500"} ${
                  isCollapsed ? "text-base" : "text-sm"
                }`}
              />
            </div>
            {!isCollapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span
                  className={`text-xs truncate ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                >
                  {user?.name || trd("用户")}
                </span>
                <button
                  onClick={logout}
                  className={`text-[10px] text-left transition-colors ${
                    isLight
                      ? "text-[#c5c5c7] hover:text-[#6e6e73]"
                      : "text-silver-400 hover:text-silver-300"
                  }`}
                >
                  {trd("退出登录")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => navigate("/login")}
            className={`w-full flex items-center rounded-lg text-sm transition-colors cursor-pointer ${
              isCollapsed
                ? "justify-center py-2 px-1"
                : "gap-2.5 px-3 py-2"
            } ${
              isLight
                ? "text-[#4a4a4c] hover:bg-[#f5f5f7]"
                : "text-gold-400 hover:bg-white/5"
            }`}
            title={isCollapsed ? trd("登录") : undefined}
          >
            <span className="flex items-center justify-center w-5 h-5">
              <i className="ri-login-box-line text-base" />
            </span>
            {!isCollapsed && <span>{trd("登录")}</span>}
          </button>
        )}
      </div>
    </aside>
  );
}// hmr-test 1787541991
